<template>
  <div
    class="qty-stepper"
    :class="{ 'qty-stepper--sm': size === 'sm', 'qty-stepper--disabled': disabled }"
  >
    <button
      type="button"
      class="qty-stepper__btn"
      :disabled="disabled || !canDecrement"
      :aria-label="$t('dashboard.tickets_quantity_decrease', 'Decrease quantity')"
      @click.prevent="step(-1)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M19 13H5v-2h14v2z"></path>
      </svg>
    </button>

    <input
      class="qty-stepper__input"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      :value="value"
      :disabled="disabled"
      :aria-label="$t('dashboard.tickets_form_quantity')"
      @input="onInput"
      @blur="onBlur"
    />

    <button
      type="button"
      class="qty-stepper__btn"
      :disabled="disabled || !canIncrement"
      :aria-label="$t('dashboard.tickets_quantity_increase', 'Increase quantity')"
      @click.prevent="step(1)"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"></path>
      </svg>
    </button>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [Number, String],
      default: 1,
    },
    min: {
      type: Number,
      default: 1,
    },
    // null or 0 means "no upper limit"
    max: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "",
    },
  },

  computed: {
    numericValue() {
      const n = Number(this.value);
      return Number.isFinite(n) ? n : 0;
    },

    hasMax() {
      return Number.isFinite(this.max) && this.max > 0;
    },

    canDecrement() {
      return this.numericValue > this.min;
    },

    canIncrement() {
      return !this.hasMax || this.numericValue < this.max;
    },
  },

  methods: {
    clamp(n) {
      let v = Number.isFinite(n) ? Math.floor(n) : this.min;
      if (v < this.min) {
        v = this.min;
      }
      if (this.hasMax && v > this.max) {
        v = this.max;
      }
      return v;
    },

    step(delta) {
      this.$emit("input", this.clamp(this.numericValue + delta));
    },

    onInput(e) {
      const raw = e.target.value.replace(/\D/g, "");
      // Keep the visible text in sync when non-digits were stripped
      if (raw !== e.target.value) {
        e.target.value = raw;
      }
      this.$emit("input", raw === "" ? "" : Number(raw));
    },

    onBlur() {
      const clamped = this.clamp(this.numericValue);
      if (clamped !== this.value) {
        this.$emit("input", clamped);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
$stepper-border: #ced4da;
$stepper-focus: #0084af;

.qty-stepper {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 50px;
  border: 1px solid $stepper-border;
  border-radius: 4px;
  background-color: #ffffff;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover,
  &:focus-within {
    border-color: $stepper-focus;
  }

  &--disabled {
    background-color: $bg-50;
    &:hover,
    &:focus-within {
      border-color: $stepper-border;
    }
  }

  &__btn {
    flex: 0 0 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background-color: transparent;
    color: $bb-100;
    cursor: pointer;
    transition: background-color 0.2s;

    > svg {
      width: 20px;
      height: 20px;
      fill: currentColor;
    }

    &:hover:not(:disabled) {
      background-color: $bg-50;
    }

    &:focus {
      outline: 0;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }

  // Second selector outweighs the global `.form-group input` rule from _ui.scss
  > .qty-stepper__input,
  .form-group & > input.qty-stepper__input {
    flex: 1 1 auto;
    min-width: 0;
    width: auto;
    height: 100%;
    margin: 0;
    padding: 0 4px;
    border: none;
    border-left: 1px solid $stepper-border;
    border-right: 1px solid $stepper-border;
    border-radius: 0;
    background-color: transparent;
    text-align: center;
    color: #20434e;
    font-size: 16px;
    font-weight: 600;
    cursor: text;

    &:focus {
      outline: 0;
      box-shadow: none;
    }

    &:disabled {
      cursor: default;
    }
  }

  &--sm {
    height: 36px;
    max-width: 150px;

    .qty-stepper__btn {
      flex-basis: 34px;
      > svg {
        width: 18px;
        height: 18px;
      }
    }

    > .qty-stepper__input,
    .form-group & > input.qty-stepper__input {
      font-size: 14px;
    }
  }
}
</style>
